import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { cn } from '../../lib/utils';

import { Tooltip } from './Tooltip';

const SettingsTabs = ({
  tabs,
  activeTab,
  setActiveTab,
  className,
  tabClassName,
  indicatorClassName,
  activeTabClassName,
  inactiveTabClassName,
  cornerRadius = 6,
}) => {
  const containerRef = useRef(null);
  const tabRefs = useRef({});
  const hasInitialized = useRef(false);
  const [indicatorStyle, setIndicatorStyle] = useState({ top: 0, left: 0, width: 0, height: 0 });

  const measureIndicator = useCallback(() => {
    const activeEl = tabRefs.current[activeTab];
    if (!activeEl) return;
    setIndicatorStyle((prev) => {
      const next = {
        top: activeEl.offsetTop,
        left: activeEl.offsetLeft,
        width: activeEl.offsetWidth,
        height: activeEl.offsetHeight,
      };
      if (prev.top === next.top && prev.left === next.left && prev.width === next.width && prev.height === next.height) {
        return prev;
      }
      return next;
    });
  }, [activeTab]);

  useLayoutEffect(() => {
    measureIndicator();

    if (!hasInitialized.current) {
      requestAnimationFrame(() => {
        hasInitialized.current = true;
      });
    }
  }, [measureIndicator]);

  // Container width can change without activeTab changing (e.g. the workspace rail being resized) — re-measure so the indicator tracks the active tab's new offsetLeft/width.
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => measureIndicator());
    observer.observe(container);
    return () => observer.disconnect();
  }, [measureIndicator]);

  const tabHandlers = useMemo(() => {
    const handlers = {};
    tabs.forEach((tab) => {
      handlers[tab.id] = {
        ref: (el) => {
          tabRefs.current[tab.id] = el;
        },
        onClick: () => {
          setActiveTab(tab.id);
        },
      };
    });
    return handlers;
  }, [tabs, setActiveTab]);

  const indicatorStyleWithTransition = {
    top: indicatorStyle.top,
    left: indicatorStyle.left,
    width: indicatorStyle.width,
    height: indicatorStyle.height,
    borderRadius: cornerRadius,
    transition: hasInitialized.current
      ? 'top 200ms var(--ease-fluid), left 200ms var(--ease-fluid), width 200ms var(--ease-fluid), height 200ms var(--ease-fluid)'
      : 'none',
  };

  const tabButtonClassName = (tab) =>
    cn(
      'relative inline-flex cursor-pointer items-center justify-center px-3 py-2 text-cursed',
      tabClassName,
      activeTab === tab.id
        ? cn('font-medium text-white dark:text-black', activeTabClassName)
        : cn('font-medium text-gray-500 dark:bg-neutral-800/60 dark:text-neutral-200', inactiveTabClassName),
    );

  const indicator = <div className={cn('absolute bg-black dark:bg-white', indicatorClassName)} style={indicatorStyleWithTransition} />;

  const tabButtons = tabs.map((tab) => {
    const buttonProps = {
      type: 'button',
      ref: tabHandlers[tab.id].ref,
      onClick: tabHandlers[tab.id].onClick,
      'data-app-region': 'none',
      // A floating tooltip replaces the native title so the two never show together.
      title: tab.tooltip ? undefined : tab.title || tab.ariaLabel || (typeof tab.label === 'string' ? tab.label : tab.id),
      'aria-label': tab.ariaLabel || tab.title || (typeof tab.label === 'string' ? tab.label : tab.id),
      className: tabButtonClassName(tab),
      style: { borderRadius: cornerRadius },
    };

    const button = (
      <button key={tab.id} {...buttonProps}>
        {tab.label}
      </button>
    );

    if (!tab.tooltip) return button;

    // The wrapper takes over as the flex child, so it must grow exactly like the
    // button it replaces (rail tabs use flex-1); the button then fills the wrapper.
    return (
      <Tooltip key={tab.id} label={tab.tooltip} className="flex min-w-0 flex-1">
        {button}
      </Tooltip>
    );
  });

  return (
    <div ref={containerRef} className={cn('relative flex gap-1', className)} style={{ borderRadius: cornerRadius }}>
      {indicator}
      {tabButtons}
    </div>
  );
};

export default SettingsTabs;
